import { useState, useCallback } from 'react';
import { pw9233Api } from '../../../services/pw9233Api';

/** Composite key identifier: "empCode|yearMonth" */
type RecordKey = string;

function makeKey(empCode: string, yearMonth: string): RecordKey {
  return `${empCode}|${yearMonth}`;
}

function parseKey(key: RecordKey): [string, string] {
  const idx = key.indexOf('|');
  return [key.slice(0, idx), key.slice(idx + 1)];
}

export function usePW9233Navigation(
  onNavigate: (empCode: string, yearMonth: string) => Promise<void>,
) {
  const [allKeys, setAllKeys] = useState<RecordKey[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);

  /** Load the full list of record keys */
  const loadList = useCallback(async (): Promise<RecordKey[]> => {
    const res = await pw9233Api.list(1, 10000);
    const keys = (res.data || []).map(r => makeKey(r.f9430_01, r.f9430_02)).filter(k => k !== '|');
    setAllKeys(keys);
    return keys;
  }, []);

  /** Navigate to a specific index */
  const navTo = useCallback(async (idx: number, isEditing: boolean) => {
    if (idx < 0 || idx >= allKeys.length || isEditing) return;
    setCurrentIdx(idx);
    const [empCode, yearMonth] = parseKey(allKeys[idx]);
    await onNavigate(empCode, yearMonth);
  }, [allKeys, onNavigate]);

  return {
    allKeys, currentIdx,
    setAllKeys, setCurrentIdx,
    loadList, navTo,
  };
}

export { parseKey };
